import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { listPaymentTerms, type PaymentTermDto } from './api';
import { PaymentTermFormModal } from './PaymentTermFormModal';

type Scope = 'active' | 'all';

/**
 * Payment term maintenance — the current product's Term screen.
 *
 * Starts empty on a new tenant and stays that way until somebody adds one: terms live in the
 * tenant's own database, so this list is theirs rather than something the platform seeds.
 */
export function PaymentTermsRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<PaymentTermDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');

  const canManage = permissions.has('tenant:manage_settings');

  const query = useQuery({
    queryKey: ['payment-terms', scope] as const,
    queryFn: ({ signal }) => listPaymentTerms(scope === 'all', signal),
  });

  function startCreate() {
    setEditing(null);
    open();
  }

  function startEdit(term: PaymentTermDto) {
    setEditing(term);
    open();
  }

  function onSaved() {
    void queryClient.invalidateQueries({ queryKey: ['payment-terms'] });
  }

  const rows = query.data ?? [];

  /**
   * The code is the headline — it is what a supplier form offers and what people say out loud
   * ("they're NET30"). Status becomes an aside so an inactive term is obvious without a label.
   */
  const columns: DataColumn<PaymentTermDto>[] = useMemo(
    () => [
      {
        key: 'code',
        label: t('paymentTerms.code'),
        card: 'title',
        render: (term) => <Code>{term.paymentTermCode}</Code>,
      },
      {
        key: 'description',
        label: t('paymentTerms.description'),
        card: 'subtitle',
        render: (term) => term.displayName,
      },
      {
        key: 'netDays',
        label: t('paymentTerms.netDays'),
        render: (term) =>
          term.netDays === null ? (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ) : (
            <Text size="sm">{term.netDays}</Text>
          ),
      },
      {
        key: 'status',
        label: t('paymentTerms.status'),
        card: 'aside',
        render: (term) =>
          term.isActive ? (
            <Text size="sm" c="dimmed">
              {t('paymentTerms.activeState')}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="gray">
              {t('paymentTerms.inactiveState')}
            </Badge>
          ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.paymentTerms')}
        actions={
          canManage ? (
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('paymentTerms.new')}
            </Button>
          ) : null
        }
      />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('paymentTerms.intro')}
        </Text>

        <Stack gap={4} w="fit-content">
          <Text size="sm" fw={500}>
            {t('paymentTerms.show')}
          </Text>
          <SegmentedControl
            value={scope}
            onChange={(v) => setScope(v as Scope)}
            data={[
              { label: t('paymentTerms.activeOnly'), value: 'active' },
              { label: t('paymentTerms.all'), value: 'all' },
            ]}
          />
        </Stack>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('paymentTerms.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('paymentTerms.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {/* Empty is the normal state for a new tenant, so it says what to do rather than
            looking like something failed. */}
        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {canManage ? t('paymentTerms.emptyManage') : t('paymentTerms.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(term) => term.paymentTermId}
            minWidth={560}
            actionsWidth={60}
            striped
            highlightOnHover
            // Undefined rather than an empty cell for a read-only session: the trailing column and
            // its blank header were pure furniture for anybody who cannot edit.
            actions={
              canManage
                ? (term) => (
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('paymentTerms.edit', { code: term.paymentTermCode })}
                      onClick={() => startEdit(term)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )
                : undefined
            }
          />
        )}
      </Stack>

      <PaymentTermFormModal opened={modalOpen} onClose={close} term={editing} onSaved={onSaved} />
    </>
  );
}
